// Libs
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import { toast } from "sonner";
// UI-Components
import { Label } from "../../../UI-Components/Label";
import { Input } from "../../../UI-Components/Input";
import { Button } from "../../../UI-Components/Button";
import Spinner from "../../../UI-Components/Spinner";
// Types
import { UpdateUserPasswordForm } from "../../../Libs/Types/UpdateUserPassword";
import {
  FailureResponseWrapper,
  SuccessResponseWrapper,
} from "../../../Libs/Types/API-Responses";
import { ChangePasswordPayload } from "../../../Libs/Types/API-Payloads";
// Endpoints
import { apiChangePassword } from "../../../Endpoints/Auth";

export default function UpdateUserPassword({
  handleCancelUpdatePassword,
}: {
  handleCancelUpdatePassword: () => void;
}) {
  const initialValues: UpdateUserPasswordForm = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const updatePasswordValidationSchema = Yup.object().shape({
    currentPassword: Yup.string()
      .min(4, "Password must be at least 4 characters")
      .required("Password is required"),
    newPassword: Yup.string()
      .min(4, "Password must be at least 4 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm Password is required"),
  });

  // API Response Handling
  const {
    mutateAsync: updateUserPassword,
    isPending: isPasswordUpdatePending,
  } = useMutation({
    mutationFn: apiChangePassword,
    retry: 0,
    onSuccess: async (data) => {
      const respData: SuccessResponseWrapper<string> = data.data;
      toast.success(respData.data ?? "Update Success", {
        duration: 3000,
      });
      handleCancelUpdatePassword();
    },
    onError: (error: any) => {
      const errorData: FailureResponseWrapper = error?.response?.data;
      toast.error(errorData.message ?? "Something Went Wrong", {
        duration: 3000,
      });
    },
    throwOnError: false,
  });

  // Handle Update Password Submit
  const handleUpdatePassword = async (values: UpdateUserPasswordForm) => {
    const payload: ChangePasswordPayload = {
      old_password: values.currentPassword,
      new_password: values.newPassword,
      confirm_password: values.confirmPassword,
    };
    await updateUserPassword(payload);
  };
  return (
    <>
      <div className="px-2">
        <div className="">
          <Formik
            initialValues={initialValues}
            onSubmit={handleUpdatePassword}
            validationSchema={updatePasswordValidationSchema}
          >
            {({ isValid }) => (
              <Form className="w-full">
                <div tabIndex={0} style={{ height: 0, width: 0, opacity: 0 }} />
                <div className="grid gap-x-4">
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="currentPassword"
                    >
                      Current Password
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      // required
                      placeholder="Enter Current Password"
                    />
                    <ErrorMessage
                      name="currentPassword"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="newPassword"
                    >
                      New Password
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      // required
                      placeholder="Enter New Password"
                    />
                    <ErrorMessage
                      name="newPassword"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="confirmPassword"
                    >
                      Confirm Password
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      // required
                      placeholder="Enter Confirm Password"
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-between gap-2">
                  <Button
                    type="submit"
                    variant={"default"}
                    disabled={!isValid}
                    className="w-full"
                  >
                    {isPasswordUpdatePending ? (
                      <Spinner className="w-6 h-6" />
                    ) : (
                      "Update"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant={"cancel"}
                    className="w-full"
                    onClick={handleCancelUpdatePassword}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}
